import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';

export interface UserSettings {
  id: string;
  user_id: string;
  tenant_id: string | null;
  email_notifications: boolean;
  push_notifications: boolean;
  weekly_report: boolean;
  monthly_report: boolean;
  alert_high_churn: boolean;
  alert_low_revenue: boolean;
  alert_goal_achieved: boolean;
  churn_threshold: number;
  theme: string;
  timezone: string;
  language: string;
  two_factor_enabled: boolean;
}

export function useSettings() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      
      // Create default settings if none exist
      if (!data) {
        const { data: newSettings, error: insertError } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            tenant_id: tenant?.id || null,
          })
          .select()
          .single();
        
        if (insertError) throw insertError;
        return newSettings as UserSettings;
      }
      
      return data as UserSettings;
    },
    enabled: !!user,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<UserSettings>) => {
      if (!user || !settings) throw new Error('Usuário não autenticado');
      
      const { error } = await supabase
        .from('user_settings')
        .update(updates)
        .eq('user_id', user.id);
      
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Configurações atualizadas com sucesso!');
      
      // Se mudou o idioma, recarrega a página após mostrar o toast
      if ('language' in variables) {
        setTimeout(() => {
          window.location.reload();
        }, 800);
      }
    },
    onError: (error) => {
      toast.error('Erro ao atualizar configurações: ' + error.message);
    },
  });

  return {
    settings,
    isLoading,
    updateSettings,
  };
}
